import {
  AdvancedMarker,
  InfoWindow,
  Pin,
  useAdvancedMarkerRef
} from '@vis.gl/react-google-maps'
import { object } from 'prop-types'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import useTreatmentCSSClass from '@hook/useTreatmentCSSClass'
import useCallPhotosModal from '@hook/useCallPhotosModal'
import { getPhotoMapPosition } from '@util/functions'
import Div from '@ui/layout/Div'
import Buttons from '@ui/elements/Buttons'
import Image from '@ui/elements/Image'
import PhotoMarker from './PhotoMarker'

const SterilizationCallMarker = ({ sterilizationCall }) => {
  const { activeScanState } = useSelector(state => state.sterilization)
  const { mission } = useSelector(state => state.main)
  const {
    id,
    position,
    companyName,
    sterilizationAddress,
    sterilizationZipCode,
    sterilizationDistrict,
    addressResponsibleFullName,
    addressResponsibleCellPhone,
    addressResponsibleEmail,
    callType,
    unitsNumber,
    scansData,
    treatmentJournals
  } = sterilizationCall
  const { photos } = scansData
  const [t] = useTranslation('common')
  const [markerRef, marker] = useAdvancedMarkerRef()
  const [infoWindowShown, setInfoWindowShown] = useState(false)
  const [getTreatmentCSSClass] = useTreatmentCSSClass()
  const [showPhotosModal, photosModal] = useCallPhotosModal()
  const [isActiveMarker, toggleMarker] = useState(true)
  const [activePhotoId, setActivePhotoId] = useState(null)
  const className = getTreatmentCSSClass(treatmentJournals, photos.length)
  const missionSubjectConfig = mission.missionSubjects?.[0]?.config
  const isConfigEnabled = value => true === value || 1 === parseInt(value)
  const withScanData = !missionSubjectConfig
    || (isConfigEnabled(missionSubjectConfig.withUnits) && isConfigEnabled(missionSubjectConfig.withEggs))
  const handleMarkerClick = useCallback(
    () => setInfoWindowShown(isShown => !isShown),
    []
  )

  const handleClose = useCallback(() => setInfoWindowShown(false), [])

  const getPin = () => {
    const pinClasses = {
      'non-scanned': { background: '#fff', borderColor: '#F9A33D', glyphColor: '#676767' },
      'is-visited': { background: '#efe9ae', borderColor: '#78BE83', glyphColor: '#444444' },
      'is-first-scanned': { background: '#F9A33D', borderColor: '#444444', glyphColor: '#d6c11b' },
      'is-second-scanned': { background: '#07adf5', borderColor: '#444444', glyphColor: '#d6c11b' },
      'is-full-scanned': { background: '#6bc242', borderColor: '#444444', glyphColor: '#f1efef' },
    }
    const colors = pinClasses[isActiveMarker && activeScanState ? activeScanState : className]

    return <Pin
      background={colors.background}
      borderColor={colors.borderColor}
      glyphColor={colors.glyphColor}
    />
  }

  const getLocatedPhotos = () => {
    const scanStatePhotoProp = {
      'is-first-scanned': 'isFirstScan',
      'is-second-scanned': 'isSecondScan'
    }

    return Object.values(photos)
      .filter(photo => !scanStatePhotoProp[activeScanState] || photo[scanStatePhotoProp[activeScanState]])
      .map(photo => ({ ...photo, mapPosition: getPhotoMapPosition(photo.position) }))
      .filter(photo => photo.mapPosition)
  }

  const checkMarker = () => {
    const fullScannedStates = ['is-second-scanned', 'is-first-scanned']

    toggleMarker(!activeScanState
      || className === activeScanState
      || fullScannedStates.includes(activeScanState))
  }

  useEffect(() => {
    if (activeScanState) {
      checkMarker()
    } else {
      toggleMarker(true)
    }

  }, [activeScanState])

  const renderPhotosButton = () => {
    return Object.values(photos).length
      ? <Buttons buttons={{
        icon: 'icon-camera',
        cssClass: 'is-info is-small',
        text: t('actions.scan_photos'),
        callback: () => showPhotosModal(
          id,
          photos,
          activeScanState,
          withScanData ? null : `${sterilizationZipCode}, ${sterilizationAddress}${sterilizationDistrict ? `, ${sterilizationDistrict}` : ''}`
        )
      }} />
      : <></>
  }

  const renderInfoContent = (photo = null) => {
    if (!withScanData) {
      return (
        <>
          <p>{companyName ? `${companyName},` : ''}{`${sterilizationZipCode}, ${sterilizationAddress}, ${sterilizationDistrict ?? ''}`}</p>
          {photo && <p>{`${t('table.photo_position')} : ${photo.position.lat}, ${photo.position.lng}`}</p>}
          {renderPhotosButton()}
        </>
      )
    }

    return (
      <>
        {photo && <Image src={photo.scanPhoto} cssImageClass='marker-scan-photo' />}
        <h2>{addressResponsibleFullName}</h2>
        <p>
          {companyName ? `${companyName},` : ''}{`${sterilizationZipCode}, ${sterilizationAddress}, ${sterilizationDistrict ?? ''}`}<br />
          {`${addressResponsibleCellPhone}, ${addressResponsibleEmail ?? ''}`}<br />
          {`${t('table.CallerType')} : ${t(`table.${callType}`)}`}<br />
          {unitsNumber ? `${t('table.Units')}: ${unitsNumber}` : ''}
        </p>
        <Div cssClass='columns marker-scan-data'>
          {scansData.isFirstScanned && activeScanState !== 'is-second-scanned'
            ? <Div cssClass='column'>
              <h4>{t('table.1st_scan')}</h4>
              <h5><span>{t('table.found_nests')}</span> :{scansData.firstScan.totalUnits}</h5>
              <h5><span>{t('table.empty_nests')}</span> :{scansData.firstScan.totalEmptyUnits}</h5>
              <h5><span>{t('table.nb_eggs')}</span> :{scansData.firstScan.totalEggs}</h5>
              <h5><span>{t('table.nb_non_treated_eggs')}</span> :{scansData.firstScan.totalNonTreatedEggs}</h5>
              <h5><span>{t('table.nb_birds')}</span> :{scansData.firstScan.totalBirds}</h5>
            </Div>
            : <></>
          }
          {scansData.isSecondScanned && activeScanState !== 'is-first-scanned'
            ? <Div cssClass='column'>
              <h4>{t('table.2nd_scan')}</h4>
              <h5><span>{t('table.found_nests')}</span> :{scansData.secondScan.totalUnits}</h5>
              <h5><span>{t('table.empty_nests')}</span> :{scansData.secondScan.totalEmptyUnits}</h5>
              <h5><span>{t('table.nb_eggs')}</span> :{scansData.secondScan.totalEggs}</h5>
              <h5><span>{t('table.nb_non_treated_eggs')}</span> :{scansData.secondScan.totalNonTreatedEggs}</h5>
              <h5><span>{t('table.nb_birds')}</span> :{scansData.secondScan.totalBirds}</h5>
            </Div>
            : <></>
          }
        </Div>
        {renderPhotosButton()}
      </>
    )
  }

  const renderPhotoMarkers = locatedPhotos => {
    const pin = getPin()

    return locatedPhotos.map(photo => <PhotoMarker
      key={`call-${id}-photo-marker-${photo.id}`}
      position={photo.mapPosition}
      pin={pin}
      isOpen={activePhotoId === photo.id}
      onClick={() => setActivePhotoId(photoId => photoId === photo.id ? null : photo.id)}
      onClose={() => setActivePhotoId(null)}
    >
      {renderInfoContent(photo)}
    </PhotoMarker>)
  }

  const renderAddressMarker = () => {
    return (
      <>
        <AdvancedMarker
          ref={markerRef}
          position={position}
          onClick={handleMarkerClick}
        >
          {getPin()}
        </AdvancedMarker>
        {infoWindowShown && (
          <InfoWindow anchor={marker} onClose={handleClose}>
            {renderInfoContent()}
          </InfoWindow>
        )}
      </>
    )
  }

  const renderMarkers = () => {
    const locatedPhotos = 'map' === mission.photoPresentation ? getLocatedPhotos() : []

    return locatedPhotos.length ? renderPhotoMarkers(locatedPhotos) : renderAddressMarker()
  }

  return (
    <>
      {isActiveMarker
        ? <>
          {renderMarkers()}
          {photosModal}
        </>
        : <></>}

    </>
  )
}

SterilizationCallMarker.propTypes = {
  sterilizationCall: object
}

export default SterilizationCallMarker